import React, { useEffect, useState, useCallback, useRef } from "react";
import { ASSESSMENT_TYPES, SITE_ASSESSMENT_TYPES } from "./table.utils";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { DragIndicator } from "@material-ui/icons";
import {
  ASSESSMENT_COUNTRY_COLUMNS_WIDTH,
  ASSESSMENT_STUDY_COLUMNS_WIDTH,
  FIELD_TYPES,
  REQUIRED_FIELDS_ERROR_MESSAGES,
} from "../constants";
import {
  checkNumberLength,
  checkNumberLengthAfterDecimal,
  extractNumericValue,
  isValidNumberStringValues,
  mapCurrencyValue,
  roundToDecimals,
} from "../../utils/helpers";
import { Draggable } from "@hello-pangea/dnd";
import FuzzySearchInput from "../../../../app/components/fuzzy.search.input";
import styled from "styled-components";
import { useScheduleEvents } from "../../../../app/contexts/schedule.events.context";
import RemoveButton from "../../../../app/components/buttons/remove";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

const PROCEDURE_NON_PROCEDURE_TYPES = ["Procedure", "Non Procedure"];

const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  const latestCallbackRef = useRef(callback);
  const latestArgsRef = useRef(null);

  // Keep latest callback reference updated
  useEffect(() => {
    latestCallbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (nameValue, key) => {
      // Store latest arguments
      latestArgsRef.current = {
        nameValue,
        key,
      };

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const currentArgs = latestArgsRef.current;
        latestCallbackRef.current(
          currentArgs.nameValue,
          currentArgs.key,
        );
        timeoutRef.current = null;
      }, delay);
    },
    [delay],
  );
};

export default function TableRow({
  sv,
  removeRow,
  onAssessmentsChange,
  value,
  index,
  assessmentNames,
  fieldType,
  currency,
  readOnly,
}) {
  const { specialSOEErrorMessages, setSpecialSOEErrorMessages } =
    useScheduleEvents();
  const [assessmentNameMappingValid, setAssessmentNameMappingValid] =
    useState(true);
  //const latestValuesRef = useRef({});

  const commentTypeKey = React.useMemo(() => {
    if (fieldType === FIELD_TYPES.VISIT) return "commentsAssessment";
    if (fieldType === FIELD_TYPES.COST) return "commentsCost";
    if (fieldType === FIELD_TYPES.MEDICAL) return "commentsClinicalStrategy";
    if (fieldType === FIELD_TYPES.DATA_MANAGEMENT)
      return "commentsDataManagement";
    return "";
  }, [fieldType]);

  React.useEffect(() => {
    setCommentTypeKeyState(sv[commentTypeKey] || "");
  }, [fieldType, commentTypeKey, sv]);

  const [commentTypeKeyState, setCommentTypeKeyState] = useState(sv?.[commentTypeKey] || "");
  const [assessmentNameState, setAssessmentNameState] = useState(sv?.assessmentName ?? "");
  const [assessmentCostState, setAssessmentCostState] = useState(sv?.assessmentCost || "");
  const [isEditingCost, setIsEditingCost] = useState(false);

  const debouncedOnAssessmentsChange = useDebounce(onAssessmentsChange, 500);

  const updateSpecialSOEErrorMessages = useCallback(
    (isValid, key) => {
      if (isValid) {
        if (specialSOEErrorMessages[key]?.count > 1) {
          setSpecialSOEErrorMessages({
            assessmentNameType: {
              message: REQUIRED_FIELDS_ERROR_MESSAGES.assessmentNameType,
              count: specialSOEErrorMessages[key]?.count - 1,
            },
          });
        } else setSpecialSOEErrorMessages({});
      } else {
        setSpecialSOEErrorMessages({
          assessmentNameType: {
            message: REQUIRED_FIELDS_ERROR_MESSAGES.assessmentNameType,
            count: (specialSOEErrorMessages[key]?.count || 0) + 1,
          },
        });
      }
    },
    [specialSOEErrorMessages, setSpecialSOEErrorMessages],
  );

  // Check if checkboxes should be disabled
  const shouldDisableCheckboxes = React.useMemo(() => {
    const isSiteAssessmentType = SITE_ASSESSMENT_TYPES.includes(sv.assessmentType);
    const isParticipantAssessmentType = sv.assessmentType?.startsWith("Participant Cost:");
    
    return isSiteAssessmentType || isParticipantAssessmentType;
  }, [sv.assessmentType]);

  // const handleTextFieldChange = useCallback((field, value, type = "string") => {
  //   const id = sv.scheduleOfEventsAssessmentId || sv.key;
  //   latestValuesRef.current[field] = value;

  //   startTransition(() => {
  //     // Update local state
  //     switch (field) {
  //       case 'assessmentName':
  //         setAssessmentNameState(value);
  //         break;
  //       case 'assessmentCost':
  //         setAssessmentCostState(value);
  //         break;
  //       case commentTypeKey:
  //         setCommentTypeKeyState(value);
  //         break;
  //     }

  //     // Queue the debounced update
  //     debouncedOnAssessmentsChange(field, value, id, type);
  //   });
  // }, [sv.scheduleOfEventsAssessmentId, sv.key, commentTypeKey, debouncedOnAssessmentsChange]);

  // const handleCheckboxChange = useCallback((field, checked) => {
  //   // Immediate update for boolean fields
  //   onAssessmentsChange(
  //     field,
  //     checked ? 1 : 0,
  //     sv.scheduleOfEventsAssessmentId || sv.key
  //   );
  // }, [onAssessmentsChange, sv.scheduleOfEventsAssessmentId, sv.key]);

  // const handleAssessmentTypeChange = useCallback((value) => {
  //   // Immediate update for select fields
  //   onAssessmentsChange(
  //     "assessmentType",
  //     value,
  //     sv.scheduleOfEventsAssessmentId || sv.key,
  //     "string"
  //   );
  // }, [onAssessmentsChange, sv.scheduleOfEventsAssessmentId, sv.key]);

  const renderRow = (dndProps) => {
    const { dragHandleProps, ...remainingDnDProps } = dndProps;

    return (
      <Row {...remainingDnDProps}>
        <Cell width={value === 0 ?
          ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentType :
          ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentType
        }>
          {value === 0 ? (
            <>
              <DragHandleContainer {...(readOnly ? {} : dragHandleProps)}>
                <DragIndicator style={{ color: readOnly ? '#ccc' : '#555' }} />
              </DragHandleContainer>
              <SelectContainer>
                <CustomTooltip title={sv.assessmentType || ""}>
                  <div style={{ width: "100%" }}>
                    <FormControl
                      style={{ width: "100%" }}
                      size="small"
                      variant="outlined"
                    >
                      <Select
                        id="assessment-type"
                        value={sv.assessmentType}
                        onChange={(e) => {
                          if (readOnly) return;
                          const newAssessmentType = e.target.value;
                          const procedureOrNonProcedureSelected = PROCEDURE_NON_PROCEDURE_TYPES.includes(newAssessmentType);
                          const nameValue = { "assessmentType": newAssessmentType };
                          if (procedureOrNonProcedureSelected) {
                            nameValue["overhead"] = 1;
                          } else {
                            nameValue["overhead"] = 0;
                            nameValue["exclude"] = 0;
                            nameValue["ipAdministered"] = 0;
                          }
                          onAssessmentsChange(
                            nameValue,
                            sv.scheduleOfEventsAssessmentId || sv.key,
                          );
                        }}
                        disabled={readOnly}
                        style={{
                          width: "100%",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}
                      >
                        {ASSESSMENT_TYPES.map(({ name, value }) => (
                          <MenuItem
                            value={value}
                            key={value}
                          >
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </CustomTooltip>
              </SelectContainer>
            </>
          ) : (
            <CustomTooltip title={sv.assessmentType || ""}>
              <Typography
                variant="body1"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  width: "100%",
                  padding: "5px",
                }}
              >
                {sv.assessmentType}
              </Typography>
            </CustomTooltip>
          )}
        </Cell>
        <Cell width={value === 0 ?
          ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentName :
          ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentName
        }>
          {value === 0 ? (
            <CustomTooltip title={assessmentNameState || ""}>
              <div style={{ width: "100%" }}>
                <TextField
                  value={assessmentNameState}
                  onChange={(e) => {
                    if (readOnly) return;
                    setAssessmentNameState(e.target.value);
                    debouncedOnAssessmentsChange(
                      { assessmentName: e.target.value },
                      sv.scheduleOfEventsAssessmentId || sv.key,
                    );
                  }}
                  variant="outlined"
                  size="small"
                  style={{ width: "100%" }}
                  disabled={readOnly}
                  InputProps={{ readOnly: readOnly }}
                  inputProps={{
                    maxLength: 100,
                  }}
                />
              </div>
            </CustomTooltip>
          ) : (
            <CustomTooltip title={sv.assessmentName || ""}>
              <Typography variant="body1" style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}>
                {sv.assessmentName}
              </Typography>
            </CustomTooltip>
          )}
        </Cell>
        {value === 0 && (
          <Cell width={ASSESSMENT_STUDY_COLUMNS_WIDTH.assessmentNameMapping}>
            <CustomTooltip title={sv.assessmentNameMapping || ""}>
              <div style={{ width: "100%" }}>
                <FuzzySearchInput
                  key={`${sv.assessmentType}${assessmentNameMappingValid}`}
                  options={assessmentNames}
                  style={{ width: "100%" }}
                  defaultValue={sv.assessmentNameMapping}
                  disabled={readOnly}
                  readOnly={readOnly}
                  onBlur={() => {
                    if (readOnly) return;
                    if (sv.assessmentNameMapping) {
                      let isValid = true;
                      const nameExists = !!assessmentNames.find(
                        (name) => name === sv.assessmentNameMapping,
                      );

                      isValid = nameExists;
                      if (!isValid && sv.assessmentNameMapping) {
                        updateSpecialSOEErrorMessages(
                          isValid,
                          "assessmentNameMapping",
                        );
                        setAssessmentNameMappingValid(isValid);
                      } else if (!sv.assessmentNameMapping) {
                        updateSpecialSOEErrorMessages(
                          true,
                          "assessmentNameMapping",
                        );
                        setAssessmentNameMappingValid(true);
                      } else {
                        setAssessmentNameMappingValid(isValid);
                      }
                    }
                  }}
                  onChange={(value) => {
                    if (readOnly) return;
                    onAssessmentsChange(
                      { assessmentNameMapping: value ?? "" },
                      sv.scheduleOfEventsAssessmentId || sv.key,
                    );
                    if (
                      value === "" ||
                      assessmentNames.find((name) => name === value)
                    ) {
                      setAssessmentNameMappingValid(true);
                      updateSpecialSOEErrorMessages(true, "assessmentNameMapping");
                    }
                  }}
                  message="Select assessment name from the list."
                  error={!assessmentNameMappingValid}
                />
              </div>
            </CustomTooltip>
          </Cell>
        )}
        {value !== 0 && (
          <Cell width={ASSESSMENT_COUNTRY_COLUMNS_WIDTH.assessmentCost}>
            <CustomTooltip title={isEditingCost ?
              assessmentCostState :
              mapCurrencyValue(currency, assessmentCostState) || ""
            }>
              <div style={{ width: "100%" }}>
                <TextField
                  style={{ width: "100%" }}
                  value={isEditingCost ? assessmentCostState : mapCurrencyValue(currency, assessmentCostState)}
                  onFocus={() => {
                    if (readOnly) return;
                    setIsEditingCost(true);
                  }}
                  onChange={(e) => {
                    if (readOnly) return;
                    const inputValue = e.target.value;
                    const numericValue = extractNumericValue(inputValue);
                    setAssessmentCostState(numericValue);
                  }}
                  onBlur={(e) => {
                    if (readOnly) return;
                    setIsEditingCost(false);
                    const inputValue = e.target.value.trim();
                    
                    if (inputValue === "") {
                      setAssessmentCostState("");
                      debouncedOnAssessmentsChange(
                        { assessmentCost: "0" },
                        sv.scheduleOfEventsAssessmentId || sv.key,
                      );
                      return;
                    }
                    
                    let numericValue = extractNumericValue(inputValue);
                    if (!checkNumberLengthAfterDecimal(numericValue, 2)) {
                      numericValue = roundToDecimals(numericValue, 2);
                    }
                    if (isValidNumberStringValues(numericValue) &&
                      checkNumberLength(numericValue, 8, 2)
                    ) {
                      setAssessmentCostState(numericValue);
                      debouncedOnAssessmentsChange(
                        { assessmentCost: numericValue },
                        sv.scheduleOfEventsAssessmentId || sv.key,
                      );
                    }
                  }}
                  variant="outlined"
                  size="small"
                  disabled={readOnly}
                  InputProps={{ readOnly: readOnly }}
                />
              </div>
            </CustomTooltip>
          </Cell>
        )}

        {value === 0 && (
          <>
            <Cell width={ASSESSMENT_STUDY_COLUMNS_WIDTH.overhead}>
              <CustomTooltip title={sv.overhead ? "Yes" : "No"}>
                <div>
                  <Checkbox
                    disabled={readOnly || shouldDisableCheckboxes}
                    checked={Boolean(sv.overhead)}
                    onChange={(e) => {
                      if (readOnly) return;
                      onAssessmentsChange(
                        { overhead: e.target.checked ? 1 : 0 },
                        sv.scheduleOfEventsAssessmentId || sv.key,
                      );
                    }}
                    name="overhead"
                  />
                </div>
              </CustomTooltip>
            </Cell>
            <Cell width={ readOnly ?
              ASSESSMENT_STUDY_COLUMNS_WIDTH.comments.readOnly :
              ASSESSMENT_STUDY_COLUMNS_WIDTH.comments.editable
            }>
              {value === 0 ? (
                <CustomTooltip title={commentTypeKeyState || ""}>
                  <div style={{ width: "100%" }}>
                    <TextField
                      key={commentTypeKey}
                      value={commentTypeKeyState}
                      onChange={(e) => {
                        if (readOnly) return;
                        setCommentTypeKeyState(e.target.value);
                        debouncedOnAssessmentsChange(
                          { [commentTypeKey]: e.target.value },
                          sv.scheduleOfEventsAssessmentId || sv.key,
                        );
                      }}
                      variant="outlined"
                      size="small"
                      style={{ width: "100%" }}
                      disabled={readOnly}
                      InputProps={{ readOnly: readOnly }}
                      inputProps={{
                        maxLength: 250,
                      }}
                    />
                  </div>
                </CustomTooltip>
              ) : (
                <CustomTooltip title={sv[commentTypeKey] || ""}>
                  <Typography variant="body1" style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}>
                    {sv[commentTypeKey]}
                  </Typography>
                </CustomTooltip>
              )}
            </Cell>
            {!readOnly && (<Cell width={ASSESSMENT_STUDY_COLUMNS_WIDTH.deleteButton}>
              <CustomTooltip title="Remove row">
                <span>
                  <RemoveButton
                    onClick={() => {
                      if (readOnly) return;
                      removeRow(sv.scheduleOfEventsAssessmentId || sv.key);
                    }}
                    disabled={readOnly}
                  />
                </span>
              </CustomTooltip>
            </Cell>)}
          </>
        )}
      </Row>
    );
  };

  return (
    <>
      {value === 0 ? (
        <Draggable
          draggableId={(sv.key || sv.scheduleOfEventsAssessmentId).toString()}
          index={index}
          isDragDisabled={readOnly}
        >
          {(provided) =>
            renderRow({
              dragHandleProps: readOnly ? {} : provided.dragHandleProps,
              ...provided.draggableProps,
              ref: provided.innerRef,
            })
          }
        </Draggable>
      ) : (
        renderRow({})
      )}
    </>
  );
}

const DragHandleContainer = styled.div`
  color: #555;
  width: 2.5rem;
`;

const Row = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  height: 51px;
  border-bottom: ${(p) =>
    p.noBorder ? "none" : "1px solid var(--surface-300)"};
  &:last-child {
    border: none;
  }
`;

const Cell = styled.div`
  background: ${(p) =>
    p.variant === "header" ? "var(--surface-100)" : "transparent"};
  height: 100%;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  justify-content: center;
  display: flex;
  width: ${(p) => (p.width ? p.width : "100%")};
  border-right: 1px solid var(--surface-300);
  padding: 5px;
  text-align: center;
  border-left: ${(p) =>
    p.leftBorder ? "1px solid var(--surface-300)" : "none"};
  &:last-child {
    border: ${(p) => (p.rightBorder ? "1px solid var(--surface-300)" : "none")};
  }
  border: ${(p) => (p.fullBorder ? "1px solid var(--surface-300)" : "")};
`;

const SelectContainer = styled.div`
  flex: 1;
  min-width: 0;
  padding: 5px;
  overflow: hidden;
`;